import type {
  ILitigatorCase,
  ILitigatorCaseSummary,
} from "src/interfaces/ComplianceGuide/ILitigatorSearch";
import styles from "./LitigatorSearch.module.css";

interface CaseFactsProps {
  caseData: ILitigatorCase;
  /** Null until the details have loaded, or when this case has no summary. */
  summary: ILitigatorCaseSummary | null;
  /**
   * Comma-separated statutory actions. Only the CaseDetails endpoint returns these — the
   * search response leaves `actions` empty — so this section appears once details load.
   */
  actions: string | null;
  /**
   * Whether the fetch that carries `actions` is still in flight. The section announces
   * itself and spins while it is, rather than letting the chips blink in unheralded.
   */
  isLoadingActions?: boolean;
  /** Lay the groups out across the full width, for when there is no summary beside them. */
  wide?: boolean;
}

// No fractional dollars: complaints plead round figures, and cents would imply a
// precision the filing does not have.
const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function splitField(value: string | null | undefined): string[] {
  if (!value || value.trim() === "") return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

// The window of conduct alleged against the named plaintiff. Either end can be missing
// when the filing only pins one down, and both can be the same day.
function formatOffencePeriod(
  start: string | null,
  end: string | null,
): string | null {
  if (start && end) return start === end ? start : `${start} — ${end}`;
  if (start) return `${start} onwards`;
  if (end) return `Through ${end}`;
  return null;
}

const CaseFacts = ({
  caseData,
  summary,
  actions,
  isLoadingActions = false,
  wide = false,
}: CaseFactsProps) => {
  const offencePeriod = summary
    ? formatOffencePeriod(summary.offenseStart, summary.offenseEnd)
    : null;
  const causes = splitField(caseData.cause);
  const actionCodes = splitField(actions);
  const plaintiffs = splitField(caseData.plaintiffs).join(", ");
  const attorneys = splitField(caseData.attorneys).join(", ");
  const firms = splitField(caseData.firmNames).join(", ");
  const defendants = splitField(caseData.defendants).join(", ");

  const className = wide
    ? `${styles["case-facts"]} ${styles["case-facts-wide"]}`
    : styles["case-facts"];

  return (
    <div className={className}>
      {/* Dates and counts in one group: the summary-derived conduct window and the
          filing's own dates answer the same question, so a rule between them would imply
          a distinction that isn't there. Each summary scalar is independently optional —
          a vague filing yields none of them, and they are dropped rather than padded with
          placeholders that would read as missing data. */}
      <div className={styles["fact-group"]}>
        {offencePeriod && (
          <>
            <div className={styles["fact-label"]}>Alleged conduct</div>
            <p
              className={`${styles["fact-value"]} ${styles["fact-value-mono"]}`}
            >
              {offencePeriod}
            </p>
          </>
        )}
        {summary?.allegedInstanceCount != null && (
          <>
            <div className={styles["fact-label"]}>Alleged contacts</div>
            {/* Phrased as a floor because that is what the extractor records: "at least
                14 calls" becomes 14, and a vague complaint yields null. */}
            <p className={styles["fact-value"]}>
              At least {summary.allegedInstanceCount}
            </p>
          </>
        )}
        {summary?.damages != null && (
          <>
            <div className={styles["fact-label"]}>Damages demanded</div>
            <p className={styles["fact-value"]}>
              {currencyFormat.format(summary.damages)}
            </p>
          </>
        )}
        <div className={styles["fact-pair"]}>
          <div>
            <div className={styles["fact-label"]}>Filed</div>
            <p
              className={`${styles["fact-value"]} ${styles["fact-value-mono"]}`}
            >
              {caseData.filingDate || "—"}
            </p>
          </div>
          <div>
            <div className={styles["fact-label"]}>Class action</div>
            <p className={styles["fact-value"]}>
              {caseData.classAction == null
                ? "—"
                : caseData.classAction
                  ? "Yes"
                  : "No"}
            </p>
          </div>
        </div>
      </div>

      <div className={styles["fact-group"]}>
        <div className={styles["fact-label"]}>Court</div>
        <p className={styles["fact-value"]}>{caseData.court || "—"}</p>
        {caseData.judge ? (
          <>
            <div className={styles["fact-label"]}>Judge</div>
            <p className={styles["fact-value"]}>{caseData.judge}</p>
          </>
        ) : (
          <p className={`${styles["fact-value"]} ${styles["fact-absent"]}`}>
            Judge not listed
          </p>
        )}
      </div>

      <div className={styles["fact-group"]}>
        <div className={styles["fact-label"]}>Plaintiff</div>
        <p className={styles["fact-value"]}>{plaintiffs || "—"}</p>
        {attorneys && (
          <>
            <div className={styles["fact-label"]}>Attorney</div>
            <p className={styles["fact-value"]}>{attorneys}</p>
          </>
        )}
        {firms && (
          <>
            <div className={styles["fact-label"]}>Law firm</div>
            <p className={styles["fact-value"]}>{firms}</p>
          </>
        )}
        <div className={styles["fact-label"]}>Defendant</div>
        <p className={styles["fact-value"]}>{defendants || "—"}</p>
      </div>

      {causes.length > 0 && (
        <div className={styles["fact-group"]}>
          <div className={styles["fact-label"]}>Cause</div>
          <div className={styles["fact-chips"]}>
            {causes.map((cause) => (
              <span key={cause} className={styles["cause-chip"]}>
                {cause}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions are the one thing here that the search hit cannot supply, so this is the
          only group that has to wait. It claims its heading up front and spins underneath,
          rather than materialising complete once the fetch lands. A case with no actions —
          or a fetch that failed — drops the group entirely instead of captioning an empty
          space. */}
      {(isLoadingActions || actionCodes.length > 0) && (
        <div className={styles["fact-group"]}>
          <div className={styles["fact-label"]}>Actions</div>
          {isLoadingActions ? (
            <div className={styles["actions-placeholder"]} role="status">
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              />
              <span className="visually-hidden">Loading actions…</span>
            </div>
          ) : (
            <div className={styles["fact-chips"]}>
              {actionCodes.map((action) => (
                <span key={action} className={styles["action-chip"]}>
                  {action}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseFacts;
